/**
 * Select-only combobox for selling plans (WAI-ARIA APG pattern).
 *
 * Focus stays on the trigger and `aria-activedescendant` tracks the highlighted
 * option, so the menu markup is free to be styled like the rest of the theme.
 *
 * Emits a bubbling `change` event whose `target.value` is the selected plan id,
 * matching the native <select> it replaces.
 */

const SUPPORTS_POPOVER =
  typeof HTMLElement !== "undefined" &&
  Object.prototype.hasOwnProperty.call(HTMLElement.prototype, "popover");

const MENU_OFFSET = 4;
const MENU_MIN_HEIGHT = 120;

class PlanSelect extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.trigger = this.querySelector(".plan-select__trigger");
    this.menu = this.querySelector(".plan-select__menu");
    this.valueLabel = this.querySelector(".plan-select__value");

    if (!this.trigger || !this.menu || !this.valueLabel) return;

    this.options = Array.from(this.menu.querySelectorAll('[role="option"]'));
    if (!this.options.length) return;

    this.initialized = true;
    this.isOpen = false;
    this.searchTerm = "";
    this.searchTimer = null;

    const selectedIndex = this.options.findIndex(
      (option) => option.getAttribute("aria-selected") === "true"
    );
    this.selectedIndex = selectedIndex === -1 ? 0 : selectedIndex;
    this.activeIndex = this.selectedIndex;

    if (SUPPORTS_POPOVER) {
      this.menu.setAttribute("popover", "manual");
    } else {
      this.menu.hidden = true;
    }

    this.handleOutsidePointer = this.handleOutsidePointer.bind(this);
    this.reposition = this.reposition.bind(this);
    this.handleScroll = this.handleScroll.bind(this);

    // Closing on blur would fire before a click on an option registers, so
    // dismissal is handled by Escape, Tab and outside pointer events instead.
    this.trigger.addEventListener("click", () => this.toggle());
    this.trigger.addEventListener("keydown", (event) => this.handleKeydown(event));
    this.menu.addEventListener("click", (event) => this.handleMenuClick(event));
    this.menu.addEventListener("pointermove", (event) => this.handleMenuPointerMove(event));
  }

  disconnectedCallback() {
    this.teardownGlobalListeners();
  }

  get value() {
    return this.options[this.selectedIndex]?.dataset.value ?? "";
  }

  get disabled() {
    return this.trigger.disabled;
  }

  open() {
    if (this.isOpen || this.disabled) return;

    this.isOpen = true;
    this.trigger.setAttribute("aria-expanded", "true");
    this.menu.classList.remove("plan-select__menu--parked");

    if (SUPPORTS_POPOVER) {
      this.menu.showPopover();
    } else {
      this.menu.hidden = false;
    }

    this.setActiveIndex(this.selectedIndex);
    this.reposition();

    document.addEventListener("pointerdown", this.handleOutsidePointer);
    window.addEventListener("resize", this.reposition);
    // Capture phase also catches scrolling on nested containers.
    window.addEventListener("scroll", this.handleScroll, true);
  }

  close({ restoreFocus = true } = {}) {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.trigger.setAttribute("aria-expanded", "false");
    this.trigger.removeAttribute("aria-activedescendant");
    this.menu.classList.remove("plan-select__menu--parked");
    this.menu.style.clipPath = "";
    this.options[this.activeIndex]?.classList.remove("plan-select__option--active");

    if (SUPPORTS_POPOVER) {
      this.menu.hidePopover();
    } else {
      this.menu.hidden = true;
    }

    this.teardownGlobalListeners();

    if (restoreFocus) this.trigger.focus();
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  teardownGlobalListeners() {
    document.removeEventListener("pointerdown", this.handleOutsidePointer);
    window.removeEventListener("resize", this.reposition);
    window.removeEventListener("scroll", this.handleScroll, true);

    cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = null;
  }

  /**
   * The menu follows the trigger while the trigger is on screen, and is parked
   * out of sight rather than dismissed once it is not, so scrolling past it and
   * back does not cost a second click to reopen.
   */
  handleScroll(event) {
    // Scrolling the menu's own list must not move it.
    if (this.menu.contains(event.target)) return;
    if (this.scrollFrame) return;

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      if (!this.isOpen) return;

      const offScreen = this.isTriggerOffScreen();

      this.menu.classList.toggle("plan-select__menu--parked", offScreen);
      if (!offScreen) this.reposition();
    });
  }

  isTriggerOffScreen() {
    const rect = this.trigger.getBoundingClientRect();

    return rect.bottom <= 0 || rect.top >= window.innerHeight;
  }

  /**
   * Anchors the menu directly under the trigger. The menu never flips above it;
   * when space is tight it shrinks and scrolls instead.
   */
  reposition() {
    if (!this.isOpen) return;

    const rect = this.trigger.getBoundingClientRect();
    const available = window.innerHeight - rect.bottom - MENU_OFFSET * 2;

    this.menu.style.maxHeight = `${Math.max(available, MENU_MIN_HEIGHT)}px`;

    if (!SUPPORTS_POPOVER) return;

    const top = rect.bottom + MENU_OFFSET;

    this.menu.style.width = `${rect.width}px`;
    this.menu.style.left = `${rect.left}px`;
    this.menu.style.top = `${top}px`;

    this.clipBehindObstruction(top);
  }

  /**
   * The menu paints in the top layer, which nothing in the document can cover
   * and z-index has no say over. Clipping away the part that falls behind a
   * sticky header gets the same result: the menu scrolls under the header
   * rather than across it, and stays open the whole time.
   */
  clipBehindObstruction(top) {
    const covered = Math.max(0, this.obstructionBottom() - top);

    this.menu.style.clipPath = covered > 0 ? `inset(${covered}px 0 0 0)` : "";
  }

  /**
   * Whatever sticky or fixed element is painted behind the menu's top edge is
   * the thing to clip against. Ancestors of the trigger are skipped: a sticky
   * product column scrolls with the menu and is not something to hide behind.
   */
  obstructionBottom() {
    const rect = this.menu.getBoundingClientRect();

    // elementsFromPoint gives the whole stack, so the menu can be skipped
    // without having to hide it first to see what is underneath.
    const behind = document
      .elementsFromPoint(rect.left + rect.width / 2, rect.top + 1)
      .find((element) => !this.menu.contains(element));

    for (let element = behind; element; element = element.parentElement) {
      const { position } = getComputedStyle(element);
      const isPinned = position === "fixed" || position === "sticky";

      if (isPinned && !element.contains(this.trigger)) {
        return element.getBoundingClientRect().bottom;
      }
    }

    return 0;
  }

  setActiveIndex(index) {
    const nextIndex = Math.max(0, Math.min(index, this.options.length - 1));

    this.options[this.activeIndex]?.classList.remove("plan-select__option--active");
    this.activeIndex = nextIndex;

    const activeOption = this.options[nextIndex];
    if (!activeOption) return;

    activeOption.classList.add("plan-select__option--active");
    this.trigger.setAttribute("aria-activedescendant", activeOption.id);
    activeOption.scrollIntoView({ block: "nearest" });
  }

  selectIndex(index) {
    const option = this.options[index];
    if (!option) return;

    const changed = index !== this.selectedIndex;

    this.options[this.selectedIndex]?.setAttribute("aria-selected", "false");
    option.setAttribute("aria-selected", "true");
    this.selectedIndex = index;

    this.valueLabel.textContent = option.dataset.label || option.textContent.trim();

    if (changed) this.dispatchEvent(new Event("change", { bubbles: true }));
  }

  handleMenuClick(event) {
    const option = event.target.closest('[role="option"]');
    if (!option) return;

    this.selectIndex(this.options.indexOf(option));
    this.close();
  }

  handleMenuPointerMove(event) {
    const option = event.target.closest('[role="option"]');
    if (!option) return;

    this.setActiveIndex(this.options.indexOf(option));
  }

  handleOutsidePointer(event) {
    if (this.contains(event.target) || this.menu.contains(event.target)) return;
    this.close({ restoreFocus: false });
  }

  handleKeydown(event) {
    const { key, altKey } = event;

    if (!this.isOpen) {
      if (key === "ArrowDown" || key === "ArrowUp" || key === "Enter" || key === " ") {
        event.preventDefault();
        this.open();
      }
      return;
    }

    switch (key) {
      case "ArrowDown":
        event.preventDefault();
        if (altKey) {
          this.selectIndex(this.activeIndex);
          this.close();
        } else {
          this.setActiveIndex(this.activeIndex + 1);
        }
        return;
      case "ArrowUp":
        event.preventDefault();
        this.setActiveIndex(this.activeIndex - 1);
        return;
      case "Home":
        event.preventDefault();
        this.setActiveIndex(0);
        return;
      case "End":
        event.preventDefault();
        this.setActiveIndex(this.options.length - 1);
        return;
      case "Enter":
      case " ":
        event.preventDefault();
        this.selectIndex(this.activeIndex);
        this.close();
        return;
      case "Escape":
        event.preventDefault();
        this.close();
        return;
      case "Tab":
        // Matches native select behaviour: commit, then let focus move on.
        this.selectIndex(this.activeIndex);
        this.close({ restoreFocus: false });
        return;
      default:
        if (key.length === 1 && !altKey && !event.ctrlKey && !event.metaKey) {
          this.handleTypeahead(key);
        }
    }
  }

  handleTypeahead(character) {
    clearTimeout(this.searchTimer);
    this.searchTerm += character.toLowerCase();
    this.searchTimer = setTimeout(() => {
      this.searchTerm = "";
    }, 500);

    const match = this.options.findIndex((option) =>
      (option.dataset.label || option.textContent).trim().toLowerCase().startsWith(this.searchTerm)
    );

    if (match !== -1) this.setActiveIndex(match);
  }
}

if (!customElements.get("plan-select")) {
  customElements.define("plan-select", PlanSelect);
}
